import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { FunctionsError } from "../lib/functions-client";
import { getBillingStatus, startCheckout, syncCheckout, openPortal, type BillingStatus } from "../lib/billing";

const PRO_FEATURES = [
  "Post unlimited jobs and shipments",
  "Reach a verified network of drivers & carriers",
  "Full performance analytics on your postings",
  "Direct messaging with applicants & bidders",
  "One flat price — no per-user fees",
  "Cancel anytime",
];

function msg(e: unknown): string {
  return e instanceof FunctionsError ? e.message : "Something went wrong. Please try again.";
}

export function Billing() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const sid = params.get("session_id");
      if (sid) {
        try {
          await syncCheckout(sid);
          if (!cancelled) setNote("You're on Pro — thanks for subscribing!");
        } catch {
          /* reconcile is best-effort; status still refreshes below */
        }
        setParams({}, { replace: true });
      }
      try {
        const s = await getBillingStatus();
        if (!cancelled) setStatus(s);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function upgrade() {
    setBusy(true);
    setNote(null);
    try {
      const { url } = await startCheckout();
      window.location.href = url;
    } catch (e) {
      setNote(msg(e));
      setBusy(false);
    }
  }

  async function manage() {
    setBusy(true);
    setNote(null);
    try {
      const { url } = await openPortal();
      window.location.href = url;
    } catch (e) {
      setNote(msg(e));
      setBusy(false);
    }
  }

  const active = status?.active;
  const renews = status?.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : null;

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader title="Billing" subtitle="Manage your RecruitTrucker membership." />

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : active ? (
        <Panel title="Manage subscription">
          <div className="grid gap-1">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted">Subscription</p>
            <p className="font-mono text-lg">
              Your membership: <span className="font-bold text-accent-strong">Pro (Monthly)</span>
            </p>
            {renews && <p className="font-mono text-sm text-muted">{status?.status === "canceled" ? "Access ends" : "Renews"} {renews}</p>}
          </div>
          <div className="border-t border-line pt-3">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted">Plan details</p>
            <p className="text-sm text-muted">Access all Pro features. Cancel anytime.</p>
          </div>
          <div className="border-t border-line pt-3">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted">Payment provider</p>
            <p className="font-mono text-sm text-info">stripe</p>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            <Button variant="ghost" disabled={busy} onClick={manage}>
              <span className="flex items-center gap-2"><Icon name="chevron" className="h-4 w-4 -rotate-90" /> Manage in Stripe portal</span>
            </Button>
          </div>
        </Panel>
      ) : status?.founding_active ? (
        <div className="grid justify-items-center gap-4 rounded-card border border-verified/40 bg-verified/5 p-8 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full border-2 border-verified/40 text-verified"><Icon name="verify" className="h-8 w-8" /></span>
          <h2 className="font-display text-2xl font-black tracking-tight">Founding member — free for 1 year</h2>
          <p className="max-w-sm text-sm text-muted">
            You're one of our first 1,000 members. Posting jobs and shipments is free for you
            {status.founding_until
              ? <> until <b className="text-ink">{new Date(status.founding_until).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</b> — no subscription needed.</>
              : <> for your first year — no subscription needed.</>}
          </p>
          <ul className="grid gap-1.5 text-left font-mono text-sm">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-muted"><Icon name="verify" className="h-4 w-4 text-verified" /> {f}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="grid justify-items-center gap-4 rounded-card border border-accent/40 bg-accent/5 p-8 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-full border-2 border-accent/40 text-accent-strong"><Icon name="security" className="h-8 w-8" /></span>
          <h2 className="font-display text-2xl font-black tracking-tight">Upgrade to Pro</h2>
          {status?.founding_member && (
            <p className="max-w-sm rounded-lg border border-line bg-surface-2 px-3 py-2 text-[13px] text-muted">Your founding-member free year has ended. Thanks for being one of our first 1,000 — subscribe to keep posting.</p>
          )}
          <p className="max-w-sm text-sm text-muted">Post jobs and shipments and reach more qualified, verified drivers and carriers.</p>
          <p className="font-mono text-3xl font-black">$100<span className="text-base font-bold text-muted"> / month</span></p>
          <ul className="grid gap-1.5 text-left font-mono text-sm">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-muted"><Icon name="verify" className="h-4 w-4 text-verified" /> {f}</li>
            ))}
          </ul>
          <div className="pt-1"><Button disabled={busy || !status?.configured} onClick={upgrade}>{busy ? "Redirecting…" : "Upgrade to Pro"}</Button></div>
          {!status?.configured && <p className="font-mono text-xs text-muted">Billing isn't enabled yet.</p>}
        </div>
      )}

      {note && <p className="font-mono text-sm text-muted">{note}</p>}
    </div>
  );
}
